import { useEffect, useRef, useState } from 'react';

import api from '../../utils/api';
import { btnStyle, inputStyle, labelStyle } from './styles';

const boxStyle = {
  marginBottom: 8,
  padding: 10,
  border: '1px solid var(--c-border-strong)',
  borderRadius: 6,
  background: 'var(--c-bg-subtle)',
};

const linkStyle = {
  background: 'none',
  border: 'none',
  color: 'var(--c-primary-fg)',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  fontSize: 12,
};

const warnStyle = { marginBottom: 6, fontSize: 12, color: 'var(--c-warning-text-2-fg)' };

/**
 * Adding a plant the vegetation dictionary does not have yet.
 *
 * The sibling of AddRegionForm, and the same argument: shown from the wizard
 * and from the detail modal, which used to hold a copy each.
 *
 * Only the scientific name is required. The Hebrew and English names are
 * how everyone else will actually look the plant up, so the form asks for
 * them, but a person standing in a field who knows only the binomial should
 * not be blocked from recording it.
 *
 * A 409 means the server already knows this plant: it is selected as if it had
 * been picked from the suggestions, and the form says which one. What must not
 * happen is a second dictionary row for a plant that already has one, because
 * then two collections of the same species no longer look like the same
 * species.
 */
export default function AddVegForm({ t, initialQuery, seqRef, onPicked, onClose, style }) {
  const [sciName, setSciName] = useState(initialQuery);
  const [hebName, setHebName] = useState('');
  const [enName, setEnName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [similar, setSimilar] = useState([]);
  const [duplicateNotice, setDuplicateNotice] = useState('');
  const debounceRef = useRef(null);

  // See AddRegionForm: the first token of the prefill, checked on open, is the
  // only thing that catches somebody accepting the prefilled name unchanged.
  useEffect(() => {
    const firstToken = initialQuery.trim().split(/\s+/)[0];
    if (firstToken.length < 2) return;
    let live = true;
    api.get(`/vegetation-species/search?q=${encodeURIComponent(firstToken)}`)
      .then(res => { if (live) setSimilar(res.data || []); })
      .catch(() => {});
    return () => { live = false; };
    // Deliberately once, on open: later checks come from typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onSciNameChange = (e) => {
    const val = e.target.value;
    setSciName(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSimilar([]); return; }
    debounceRef.current = setTimeout(() => {
      api.get(`/vegetation-species/search?q=${encodeURIComponent(val.trim())}`)
        .then(res => setSimilar(res.data || []))
        .catch(() => setSimilar([]));
    }, 300);
  };

  const take = (sp) => { onPicked(sp); onClose(); };

  const valid = !!sciName.trim();

  const submit = () => {
    if (!valid) return;
    setSaving(true);
    setError('');
    setDuplicateNotice('');
    const seq = ++seqRef.current;
    api.post('/vegetation-species', {
      scientific_name: sciName.trim(),
      hebrew_name: hebName.trim() || null,
      common_name_en: enName.trim() || null,
    })
      .then(res => {
        if (seq !== seqRef.current) return; // widget closed/reopened while this was in flight
        take(res.data);
      })
      .catch(err => {
        if (seq !== seqRef.current) return;
        if (err.response?.status === 409 && err.response?.data?.species_id) {
          onPicked(err.response.data);
          setDuplicateNotice(t('step2.addVegDuplicateNotice'));
        } else {
          setError(err.response?.data?.error || t('step2.addVegFailed'));
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ ...boxStyle, ...style }}>
      <label style={{ ...labelStyle, marginBottom: 6 }}>
        {t('step2.addVegSciName')}
        <input value={sciName} onChange={onSciNameChange} dir="ltr" style={inputStyle} title={t('tip.vegSciName')} />
      </label>
      <label style={{ ...labelStyle, marginBottom: 6 }}>
        {t('step2.addVegHebName')}
        <input value={hebName} onChange={e => setHebName(e.target.value)} dir="rtl" style={inputStyle} title={t('tip.vegHebName')} />
      </label>
      <label style={{ ...labelStyle, marginBottom: 6 }}>
        {t('step2.addVegEnName')}
        <input value={enName} onChange={e => setEnName(e.target.value)} dir="ltr" style={inputStyle} title={t('tip.vegEnName')} />
      </label>
      {similar.length > 0 && (
        <div style={warnStyle}>
          {t('step2.addVegSimilarWarning')}
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
            {similar.map(sp => (
              <li key={sp.species_id}>
                <button type="button" onClick={() => take(sp)} style={linkStyle} title={t('tip.pickSimilarSpecies')}>
                  {sp.scientific_name}{sp.hebrew_name ? ` (${sp.hebrew_name})` : ''}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {duplicateNotice && (
        <p style={{ color: 'var(--c-warning-text-2-fg)', fontSize: 12, margin: '0 0 6px' }}>{duplicateNotice}</p>
      )}
      {error && <p style={{ color: 'red', fontSize: 12, margin: '0 0 6px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={submit} disabled={saving || !valid} title={t('tip.saveSpecies')}
          style={btnStyle(saving || !valid ? 'var(--c-text-faint)' : 'var(--c-primary)')}>
          {saving ? t('common:saving') : t('step2.addVegSave')}
        </button>
        <button type="button" onClick={onClose} style={btnStyle('var(--c-text-muted)')} title={t('tip.cancelAddForm')}>
          {t('common:cancel')}
        </button>
      </div>
    </div>
  );
}
